import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { DimensionEnums, FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { GenericFormSection, MultipleAutoCompleteExtraProps } from "models/propTypes/genericFormProps";
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ViewState } from "models/enums/incomingInvoice";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
//#endregion
//#region components
import { useSupplierService, useIncomingInvoiceService, useCompanyService } from "serviceHooks";
import useBookingTreeItems from "../bookingTree/useBookingTreeItems";
//#endregion
import { SupplierService } from "services";
import { EnumHelper, FormHelper, QueryStateHelper, UtilsHelper, FilterHelper, DateHelper } from "utils/helpers";

function useIncomingInvoiceReportFilterDefinition() {
  const incomingInvoiceService = useIncomingInvoiceService();
  const supplierService = useSupplierService();
  const companyService = useCompanyService();
  const [suggestedSuppliers, setSuggestedSuppliers] = React.useState<AutocompleteListItem[]>([]);
  const [suggestedPersons, setSuggestedPersons] = React.useState<AutocompleteListItem[]>([]);

  const { searchBookingTreeItemsAutoComplete: searchBookingTreeItems, getBookingTreeItems } = useBookingTreeItems();

  const searchReceivers = (searchText: string) => {
    return UtilsHelper.convertToResultAutoCompleteItem(
      incomingInvoiceService.getIncomingInvoicePersons(searchText, true)
    );
  };
  const searchSuppliers = (searchText: string) => {
    return UtilsHelper.convertToResultAutoCompleteItem(SupplierService.searchSupplierByName(searchText));
  };

  const isAgentSalesActive = useSelector((store: AppState) => store.auth.selectedCompany.isAgentSalesActive);

  React.useEffect(() => {
    supplierService
      .getSuggestedSuppliers()
      .then((res) =>
        setSuggestedSuppliers(res.map((supplier: any) => ({ id: supplier.id, value: supplier.name ?? "" })))
      );

    incomingInvoiceService
      .getSuggestedIncomingInvoicePersons(true)
      .then((res) => setSuggestedPersons(res.map((person) => ({ id: person.id, value: person.name ?? "" }))));
  }, []);

  const filterValueFormatter = (values: IncomingInvoiceFormTypes.Filter): IncomingInvoiceFormTypes.Filter => {
    if (values.snapshotDate && !values.isSnapShot) {
      return {
        receivers: values.receivers,
        dimensions: values.dimensions,
        snapshotDate: values.snapshotDate,
        isSnapShot: true,
      };
    } else if (!values.snapshotDate && values.isSnapShot) {
      return { ...values, isSnapShot: false };
    }

    return values;
  };

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModel.IncomingInvoiceFilter
  ): Promise<IncomingInvoiceFormTypes.Filter> => {
    const reportFilter: IncomingInvoiceFormTypes.Filter = {};

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.IncomingInvoice.Filter.InvoiceDate,
      filter.invoiceDateFrom,
      filter.invoiceDateTo
    );

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.IncomingInvoice.Filter.DueDate,
      filter.dueDateFrom,
      filter.dueDateTo
    );

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.IncomingInvoice.Filter.PaymentDate,
      filter.paymentDateFrom,
      filter.paymentDateTo
    );

    if (filter.snapshotDate !== undefined) {
      reportFilter.snapshotDate = DateHelper.fromAPIDate(filter.snapshotDate);
    }

    let suppliers: Promise<AutocompleteListItem[] | undefined>;
    const supplierIds =
      filter.supplierIds !== undefined
        ? filter.supplierIds
        : filter.supplierId !== undefined
        ? [filter.supplierId]
        : undefined;

    if (supplierIds?.length) {
      suppliers = Promise.all(
        supplierIds.map(async (id) => {
          const name = await supplierService.getSupplier(id).then((s) => s.name ?? "");
          return { id: id, value: name };
        })
      );
      reportFilter.suppliers = await suppliers;
    }

    const receiverIds =
      filter.receiverIds !== undefined
        ? filter.receiverIds
        : filter.receiverId !== undefined
        ? [filter.receiverId]
        : undefined;

    if (receiverIds?.length) {
      const receivers: Promise<AutocompleteListItem[] | undefined> = companyService
        .getEmployees({ personIds: receiverIds })
        .then((res) => res.map((person) => ({ id: person.id, value: person.name ?? "" })));

      reportFilter.receivers = await receivers;
    }

    if (filter.invoiceNumber !== undefined) {
      reportFilter.invoiceNumber = filter.invoiceNumber;
    }

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.IncomingInvoice.Filter.Amount,
      filter.minTotalAmount,
      filter.maxTotalAmount
    );

    if (filter.paymentStatuses !== undefined) {
      reportFilter.paymentStatuses = filter.paymentStatuses.map((status) => ({
        id: status,
        value: incomingInvoiceService.getPaymentStateText(status),
      }));
    }

    if (filter.states !== undefined) {
      reportFilter.state = Array.from(
        new Set(filter.states.map((state) => incomingInvoiceService.toViewState(state)))
      ).map((state) => ({
        id: state,
        value: incomingInvoiceService.getStateText(state),
      }));
    }

    let bookingAccounts: AutocompleteListItem[] = [];
    if (filter.accounts?.length) {
      bookingAccounts = await getBookingTreeItems().then((res) =>
        res
          .filter((x) => filter.accounts?.some((y) => y === x.account))
          .map((a): AutocompleteListItem => {
            return { id: a.account, value: a.name ?? "" };
          })
      );
      reportFilter.bookingAccounts = bookingAccounts;
    }

    if (filter.dimensions !== undefined) {
      reportFilter.dimensions = filter.dimensions;
    }

    if (isAgentSalesActive) {
      reportFilter.agentIncomingInvoicesId = filter.showAgentIncomingInvoices;
    }

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<IncomingInvoiceFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.Date,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        invoiceDate: {
          label: TranslationKeys.Common.InvoiceDate,
          type: FormEnums.FormFieldType.DATERANGE,
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
        },
        dueDate: {
          label: TranslationKeys.Invoice.DueDate,
          type: FormEnums.FormFieldType.DATERANGE,
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
        },
        paymentDate: {
          label: TranslationKeys.Common.PaymentDate,
          placeholder: TranslationKeys.Common.SelectDate,
          type: FormEnums.FormFieldType.DATERANGE,
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
        },
        snapshotDate: {
          label: TranslationKeys.IncomingInvoice.DateOfAccountsPayable,
          type: FormEnums.FormFieldType.DATE,
          placeholder: TranslationKeys.Common.SelectDate,
          descriptionText: TranslationKeys.IncomingInvoice.DateOfAccountsPayableHelpText,
        },
      },
    },
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        bookingAccounts: {
          label: TranslationKeys.Common.RefersTo,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            items: searchBookingTreeItems,
          } as MultipleAutoCompleteExtraProps,
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
        },
        suppliers: {
          label: TranslationKeys.Common.Sender,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          placeholder: TranslationKeys.Common.Search,
          extraProps: {
            items: searchSuppliers,
            suggestedItems: suggestedSuppliers,
          } as MultipleAutoCompleteExtraProps,
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
        },
        receivers: {
          label: TranslationKeys.Common.Receiver,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          placeholder: TranslationKeys.Common.Search,
          extraProps: {
            suggestedItems: suggestedPersons,
            items: searchReceivers,
          } as MultipleAutoCompleteExtraProps,
        },
        state: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: EnumHelper.getNumericItemValues(ViewState).map((state) => ({
              id: state,
              value: incomingInvoiceService.getStateText(state),
            })),
          },
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
        },
        paymentStatuses: {
          label: TranslationKeys.Common.PaymentStatus,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.IncomingInvoicePaymentStatus).map(
              (status) => ({
                id: status,
                value: incomingInvoiceService.getPaymentStateText(status),
              })
            ),
          },
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
        },
        invoiceNumber: {
          label: TranslationKeys.Common.OCRInvoiceNum,
          type: FormEnums.FormFieldType.TEXT,
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
        },
      },
    },
    {
      label: TranslationKeys.Common.Agent,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        agentIncomingInvoicesId: {
          label: TranslationKeys.IncomingInvoice.AgentInvoices,
          type: FormEnums.FormFieldType.SELECTION,
          extraProps: {
            items: FormHelper.getAgentSaleInvoiceItems(),
          },
        },
      },
      hidden: !isAgentSalesActive,
    },
    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        amount: {
          label: TranslationKeys.Common.TotalAmount,
          type: FormEnums.FormFieldType.NUMBERRANGE,
          disabled: (values: IncomingInvoiceFormTypes.Filter) => !!values.isSnapShot,
          extraProps: {
            allowNegative: true,
            decimalPoints: 2,
          },
        },
      },
    },
    {
      label: TranslationKeys.Common.Tags,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        dimensions: {
          label: TranslationKeys.Common.Tags,
          type: FormEnums.FormFieldType.DIMENSIONMULTIPLEINPUTSELECTOR,
          extraProps: {
            type: DimensionEnums.DimensionCategory.IncomingInvoice,
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<IncomingInvoiceFormTypes.Filter> = {
    invoiceDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.IncomingInvoice.Filter.InvoiceDate),
    dueDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.IncomingInvoice.Filter.DueDate),
    paymentDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.IncomingInvoice.Filter.PaymentDate),
    snapshotDate: QueryStateHelper.dateQueryEncoder(FormFieldConstants.IncomingInvoice.Filter.SnapshotDate),
    bookingAccounts: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.IncomingInvoice.Filter.BookingAccounts
    ),
    suppliers: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.IncomingInvoice.Filter.Suppliers
    ),
    receivers: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.IncomingInvoice.Filter.Receivers
    ),
    invoiceNumber: QueryStateHelper.stringQueryEncoder(FormFieldConstants.IncomingInvoice.Filter.InvoiceNumber),
    amount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.IncomingInvoice.Filter.Amount),
    paymentStatuses: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.IncomingInvoice.Filter.PaymentStatuses,
      EnumHelper.getNumericItemValues(WintSuperkollAPIModel.IncomingInvoicePaymentStatus).map((status) => ({
        id: status,
        value: incomingInvoiceService.getPaymentStateText(status),
      }))
    ),
    state: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.IncomingInvoice.Filter.State,
      EnumHelper.getNumericItemValues(ViewState).map((state) => ({
        id: state,
        value: incomingInvoiceService.getStateText(state),
      }))
    ),
    dimensions: QueryStateHelper.dimensionsQueryEncoder(FormFieldConstants.IncomingInvoice.Filter.Dimensions),
    agentIncomingInvoicesId: QueryStateHelper.numberQueryEncoder(
      FormFieldConstants.IncomingInvoice.Filter.AgentIncomingInvoicesId
    ),
  };

  return { filterFormFields, filterValueFormatter, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useIncomingInvoiceReportFilterDefinition;
